import React, { useState, useEffect, useRef } from 'react';
import { AiChat } from './AiChat';
import { Upload, FileText, Send, Sparkles, AlertCircle, RefreshCw, X } from 'lucide-react';
import AnalysisPreviewModal from '../modals/AnalysisPreviewModal';
import { useToast } from '../../context/ToastContext';
import { usePIM } from '../../context/PIMContext';
import ReactMarkdown from 'react-markdown';
import HelpIcon from '../ui/HelpIcon';
import { useHandbook } from '../../context/HandbookContext';

export const AiAssistantTab = () => {
    const { toast } = useToast();
    const { openHandbook } = useHandbook();
    const [isDragging, setIsDragging] = useState(false);
    const [uploadStatus, setUploadStatus] = useState(null); // null, 'uploading', 'success', 'error'
    const [statusMessage, setStatusMessage] = useState('');
    const [analysisData, setAnalysisData] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const fileInputRef = useRef(null);

    const handleDragOver = (e) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        setIsDragging(false);
    };

    const handleDrop = async (e) => {
        e.preventDefault();
        setIsDragging(false);
        const files = e.dataTransfer.files;
        if (files.length > 0) {
            await uploadFile(files[0]);
        }
    };

    const handleFileSelect = async (e) => {
        if (e.target.files.length > 0) {
            await uploadFile(e.target.files[0]);
        }
    };

    const uploadFile = async (file) => {
        setUploadStatus('uploading');
        setStatusMessage(`Analyzing ${file.name}...`);

        const formData = new FormData();
        formData.append('file', file);

        try {
            const res = await fetch('/api/ai/analyze-document', {
                method: 'POST',
                body: formData
            });
            const data = await res.json();

            if (data.success) {
                setUploadStatus('success');
                setStatusMessage(`Analysis Complete. Reviewing...`);
                setAnalysisData(data);
                setIsModalOpen(true);
            } else {
                setUploadStatus('error');
                setStatusMessage(`Analysis failed: ${data.error}`);
                toast.error(`Analysis failed: ${data.error}`);

            }
        } catch (err) {
            console.error(err);
            setUploadStatus('error');
            const erroMsg = err.message || "Network error during analysis";
            setStatusMessage(`Analysis failed: ${erroMsg}`);
            toast.error(erroMsg);

        }
    };

    const handleCommit = async (payload) => {
        try {
            const res = await fetch('/api/ai/commit', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            const data = await res.json();

            if (data.success) {
                toast.success('Document processed and data applied successfully.');

                setUploadStatus(null);
                setStatusMessage('');
            } else {
                throw new Error(data.error);
            }
        } catch (error) {
            console.error(error);
            toast.error(`Commit failed: ${error.message}`);

            throw error; // Re-throw for modal to handle if needed
        }
    };

    const handleDiscard = async (stagingId) => {
        try {
            await fetch('/api/ai/discard', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ stagingId })
            });
            toast.info('Analysis discarded.');

            setUploadStatus(null);
            setStatusMessage('');
        } catch (error) {
            console.error("Discard failed", error);
            toast.error("Failed to discard analysis");

        }
    };

    return (
        <div className="h-full flex flex-row p-6 max-w-[1600px] mx-auto gap-6 relative">

            <AnalysisPreviewModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                analysisResult={analysisData}
                onCommit={handleCommit}
                onDiscard={handleDiscard}
            />

            {/* AI Chat Layout - Left Side (3/4) */}
            <div className="flex-[3] bg-stone-900/30 rounded-xl border border-stone-800 overflow-hidden relative h-full">
                <AiChat
                    isOpen={true}
                    isEmbedded={true}
                />
            </div>

            {/* Drag & Drop Zone - Right Side (1/4) */}
            <div
                className={`
                    flex-1 flex flex-col items-center justify-center gap-4
                    border-2 border-dashed rounded-xl p-4 transition-colors cursor-pointer h-full min-h-[600px]
                    ${isDragging ? 'border-purple-500 bg-purple-900/20' : 'border-stone-700 hover:border-stone-500 bg-stone-900/50'}
                `}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current.click()}
            >
                <Upload size={48} className={`mb-2 ${isDragging ? 'text-purple-400' : 'text-stone-500'}`} />
                <h3 className="text-lg font-medium text-stone-300 text-center">Drag & Drop<br />Files here</h3>
                <p className="text-xs text-stone-500 text-center max-w-[200px]">Supports PDF. The AI will analyze and classify them.</p>
                <input
                    type="file"
                    ref={fileInputRef}
                    className="hidden"
                    onChange={handleFileSelect}
                />

                {uploadStatus && (
                    <div className={`mt-auto mb-4 w-full px-4 py-2 rounded text-sm font-medium text-center
                        ${uploadStatus === 'uploading' ? 'bg-blue-900/30 text-blue-300' : ''}
                        ${uploadStatus === 'success' ? 'bg-green-900/30 text-green-300' : ''}
                        ${uploadStatus === 'error' ? 'bg-red-900/30 text-red-300' : ''}
                    `}>
                        {statusMessage}
                    </div>
                )}
            </div>
        </div>
    );
};
